import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ReportListItem, ReportType } from "./api";
import { FlagDialog } from "./FlagDialog";
import { formatRelative } from "./relativeTime";

const TYPE_KEYS: Record<ReportType, string> = {
  available: "reports.type_available",
  charging: "reports.type_charging",
  waiting: "reports.type_waiting",
  offline: "reports.type_offline",
};

const TYPE_CLASSES: Record<ReportType, string> = {
  available: "bg-volt/15 text-volt-deep",
  charging: "bg-amber/15 text-amber",
  waiting: "bg-amber/15 text-amber",
  offline: "bg-ember/15 text-ember",
};

type Props = {
  reports: ReportListItem[];
};

export function ReportList({ reports }: Props) {
  const { t } = useTranslation();
  const [flaggingId, setFlaggingId] = useState<string | null>(null);

  if (reports.length === 0) {
    return <p className="text-sm text-fg-mute">{t("reports.no_recent")}</p>;
  }
  return (
    <>
      <ul className="space-y-2">
        {reports.map((r) => {
          const typeLabel = t(TYPE_KEYS[r.report_type]);
          return (
            <li
              key={r.id}
              className="flex items-start justify-between gap-3 rounded-md border border-line bg-surface p-2"
            >
              <div className="min-w-0 flex-1">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${TYPE_CLASSES[r.report_type]}`}
                >
                  {typeLabel}
                </span>
                <p className="mt-1 text-xs text-fg-mute">
                  {formatRelative(r.created_at)} ·{" "}
                  {r.is_anonymous ? t("common.anonymous") : t("common.user")}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2 text-xs text-fg-mute">
                <span>{t("reports.helpful", { count: r.helpful_count })}</span>
                <button
                  type="button"
                  onClick={() => setFlaggingId(r.id)}
                  className="font-medium text-fg-mute underline"
                  aria-label={t("reports.flag_label", {
                    type: typeLabel,
                    when: formatRelative(r.created_at),
                  })}
                >
                  {t("common.report")}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {flaggingId && (
        <FlagDialog reportId={flaggingId} onClose={() => setFlaggingId(null)} />
      )}
    </>
  );
}
